import React, { useState, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react';
import { cn } from '../../../../utils/cn';

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'compact' | 'floating';
  position?: 'left' | 'right';
  collapsible?: boolean;
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  overlay?: boolean;
  logo?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

const SIDEBAR_VARIANTS = {
  default: 'bg-surface-secondary border-neutral-200',
  compact: 'bg-surface-primary border-neutral-300',
  floating: 'bg-surface-elevated shadow-xl border-neutral-200/50'
};

const SIDEBAR_POSITIONS = {
  left: 'left-0 border-r',
  right: 'right-0 border-l'
};

export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(({
  variant = 'default',
  position = 'left',
  collapsible = true,
  collapsed = false,
  onCollapse,
  overlay = false,
  logo,
  footer,
  className,
  children,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(!collapsed);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onCollapse?.(newState);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {overlay && isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <motion.div
        ref={ref}
        className={cn(
          // Base styles
          'fixed top-0 bottom-0 z-50 flex flex-col',
          'transition-all duration-300 ease-in-out',
          
          // Variant styles
          SIDEBAR_VARIANTS[variant],
          
          // Position styles
          SIDEBAR_POSITIONS[position],
          
          // Width and visibility
          isOpen ? 'w-64' : 'w-16',
          overlay ? 'lg:relative lg:translate-x-0' : 'relative',
          
          // Mobile responsive
          overlay && !isOpen && '-translate-x-full lg:translate-x-0',
          
          className
        )}
        initial={false}
        animate={{
          width: isOpen ? 256 : 64,
          x: overlay && !isOpen ? (position === 'left' ? -256 : 256) : 0
        }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        {...(props as any)}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          {/* Logo */}
          <div className={cn(
            'flex items-center transition-opacity duration-200',
            !isOpen && 'opacity-0 pointer-events-none'
          )}>
            {logo}
          </div>
          
          {/* Toggle Button */}
          {collapsible && (
            <button
              onClick={handleToggle}
              className="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
              aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {isOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className={cn(
            'p-4 border-t border-neutral-200 transition-opacity duration-200',
            !isOpen && 'opacity-0 pointer-events-none'
          )}>
            {footer}
          </div>
        )}

        {/* Collapsed State Indicator */}
        {!isOpen && logo && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
            <div className="w-8 h-8 flex items-center justify-center">
              {React.isValidElement(logo) && React.cloneElement(logo as React.ReactElement, {
                className: cn((logo as any)?.props?.className, 'w-6 h-6')
              })}
            </div>
          </div>
        )}
      </motion.div>
    </>
  );
});

Sidebar.displayName = 'Sidebar';

// Sidebar Section Component
export interface SidebarSectionProps {
  title?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const SidebarSection: React.FC<SidebarSectionProps> = ({
  title,
  collapsible = false,
  defaultCollapsed = false,
  className,
  children
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <div className={cn('mb-6', className)}>
      {/* Section Header */}
      {title && (
        <div className="px-4 mb-2">
          {collapsible ? (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="flex items-center w-full text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-700 transition-colors"
            >
              <span className="flex-1">{title}</span>
              <motion.div
                animate={{ rotate: isCollapsed ? 0 : 180 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-3 h-3" />
              </motion.div>
            </button>
          ) : (
            <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
              {title}
            </h3>
          )}
        </div>
      )}

      {/* Section Content */}
      <AnimatePresence>
        {(!collapsible || !isCollapsed) && (
          <motion.div
            initial={collapsible ? { height: 0, opacity: 0 } : false}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Sidebar Item Component
export interface SidebarItemProps {
  icon?: React.ReactNode;
  label: string;
  active?: boolean;
  badge?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  className?: string;
}

export const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  active = false,
  badge,
  onClick,
  href,
  disabled = false,
  className
}) => {
  const Component = href ? 'a' : 'button';

  return (
    <Component
      href={href}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        // Base styles
        'flex items-center w-full px-4 py-2 text-sm font-medium transition-all duration-200',
        'hover:bg-neutral-100 rounded-lg mx-2 mb-1',
        'focus:outline-none focus:ring-2 focus:ring-primary-500/20',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        
        // Active state
        active && [
          'bg-primary-100 text-primary-700 shadow-sm',
          'hover:bg-primary-200'
        ],
        
        // Default state
        !active && 'text-neutral-700',
        
        className
      )}
    >
      {/* Icon */}
      {icon && (
        <span className={cn(
          'mr-3 w-5 h-5 flex-shrink-0',
          active ? 'text-primary-600' : 'text-neutral-500'
        )}>
          {icon}
        </span>
      )}

      {/* Label */}
      <span className="flex-1 truncate">{label}</span>

      {/* Badge */}
      {badge && (
        <span className="ml-2 flex-shrink-0">
          {badge}
        </span>
      )}
    </Component>
  );
};

// Sidebar Submenu Component
export interface SidebarSubmenuProps {
  icon?: React.ReactNode;
  label: string;
  defaultOpen?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const SidebarSubmenu: React.FC<SidebarSubmenuProps> = ({
  icon,
  label,
  defaultOpen = false,
  className,
  children
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={cn('mb-1', className)}>
      {/* Submenu Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center w-full px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-lg mx-2 transition-colors"
      >
        {/* Icon */}
        {icon && (
          <span className="mr-3 w-5 h-5 flex-shrink-0 text-neutral-500">
            {icon}
          </span>
        )}

        {/* Label */}
        <span className="flex-1 text-left truncate">{label}</span>

        {/* Chevron */}
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
          className="ml-2 flex-shrink-0"
        >
          <ChevronRight className="w-4 h-4" />
        </motion.div>
      </button>

      {/* Submenu Items */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden ml-8 mt-1"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Sidebar Divider Component
export const SidebarDivider: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('my-4 mx-4 border-b border-neutral-200', className)} />
);

// Mobile Sidebar Trigger Component
export interface MobileSidebarTriggerProps {
  onOpen: () => void;
  className?: string;
}

export const MobileSidebarTrigger: React.FC<MobileSidebarTriggerProps> = ({
  onOpen,
  className
}) => (
  <button
    onClick={onOpen}
    className={cn(
      'lg:hidden p-2 rounded-lg hover:bg-neutral-100 transition-colors',
      className
    )}
    aria-label="Open sidebar"
  >
    <Menu className="w-6 h-6" />
  </button>
); 